import { useCallback } from "react";
import useJouneyReq from "../../../../components/hook/bot-req";
import { useTenantId } from "../../../../components/hook/use-tenant-id";

// Real, now-live Executions dashboard API (journeybuilder.connect.dalmlabs.com)
// per "Bot Builder & Executions API — Frontend Reference" (Oct 12, 2026) —
// every field name and shape below is copied from that doc's real production
// response samples, not guessed. Same auth convention the rest of bot-studio
// already uses (journey-api.ts's useTestRunSend): Authorization: Bearer
// <portal JWT> + X-TENANT, both resolved via use-tenant-id.ts.

function authHeaders(tenantId: string) {
  const token = localStorage.getItem("authToken");
  return { Authorization: `Bearer ${token}`, "X-TENANT": tenantId };
}

export interface ExecutionsSummary {
  workflows: { active: number; inactive: number; total: number; drill_down?: string };
  executions: {
    total: number;
    running: number;
    stalled: number;
    stalled_after_days: number;
    stalled_drill_down?: string;
    completed: number;
    failed: number;
    drill_down?: string;
  };
  // null when nothing has settled yet (no completed/failed executions in the
  // window) — not the same as a real 0%, so render it as a dash, not "0%".
  success_rate: number | null;
  customers: { reached: number; unique: number; repeat: number };
}

// GET /v1/executions/summary?tenant_id=&days=30
export function useExecutionsSummary() {
  const tenantId = useTenantId();
  const { loading, makeRequest } = useJouneyReq("/v1/executions/summary", "GET", authHeaders(tenantId));
  const get = useCallback(
    async (days = 30): Promise<ExecutionsSummary | null> => {
      const [json, status] = await makeRequest(undefined, { tenant_id: tenantId, days: String(days) });
      return status >= 200 && status < 300 ? json : null;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [makeRequest, tenantId]
  );
  return { loading, get, ready: Boolean(tenantId) };
}

export interface WorkflowItem {
  journey_id: number;
  name: string;
  status: string;
  active: boolean;
  channels: string[];
  runs: number;
  customers: number;
  success_count: number;
  failed_count: number;
  success_rate: number | null;
  last_activity: string | null;
  drill_down?: string;
}

// GET /v1/executions/by-workflow?active=true&search=
export function useExecutionsByWorkflow() {
  const tenantId = useTenantId();
  const { loading, makeRequest } = useJouneyReq("/v1/executions/by-workflow", "GET", authHeaders(tenantId));
  const get = useCallback(
    async (params: { active?: boolean; search?: string }): Promise<{ total: number; items: WorkflowItem[] } | null> => {
      const query: Record<string, string> = {};
      if (params.active !== undefined) query.active = String(params.active);
      if (params.search) query.search = params.search;
      const [json, status] = await makeRequest(undefined, query);
      return status >= 200 && status < 300 ? json : null;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [makeRequest]
  );
  return { loading, get, ready: Boolean(tenantId) };
}

export interface ChannelBar {
  channel: string;
  executions: number;
  percent: number;
}

// GET /v1/executions/by-channel — "unspecified" is a real channel value (a
// journey nobody set a channel on), not an error state.
export function useExecutionsByChannel() {
  const tenantId = useTenantId();
  const { loading, makeRequest } = useJouneyReq("/v1/executions/by-channel", "GET", authHeaders(tenantId));
  const get = useCallback(async (): Promise<{ total: number; channels: ChannelBar[] } | null> => {
    const [json, status] = await makeRequest();
    return status >= 200 && status < 300 ? json : null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [makeRequest]);
  return { loading, get, ready: Boolean(tenantId) };
}

export interface ExecutionItem {
  session_id: string;
  journey_id: number;
  contact_id: string;
  trigger_event: string;
  status: string; // "running" | "waiting" | "failed" | "completed"
  current_step: string;
  next_step: string | null;
  next_step_time: string | null;
  started_at: string;
  duration_seconds: number;
}

export interface ExecutionsListResult {
  page: number;
  per_page: number;
  total: number;
  tabs: { running: number; waiting: number; failed: number; completed: number };
  items: ExecutionItem[];
}

// GET /v1/executions?search=&journey_id=&status=&stalled=&page=1&per_page=25
// `search` matches the execution id OR the contact id — one box, both
// fields. `tabs` is computed server-side with the same search/journey_id/
// stalled filters as `items`, just never narrowed by `status` itself, so a
// tab's count can never disagree with what clicking it shows.
export function useExecutionsList() {
  const tenantId = useTenantId();
  const { loading, makeRequest } = useJouneyReq("/v1/executions", "GET", authHeaders(tenantId));
  const get = useCallback(
    async (params: {
      search?: string;
      journey_id?: number;
      status?: string;
      stalled?: boolean;
      page?: number;
      per_page?: number;
    }): Promise<ExecutionsListResult | null> => {
      const query: Record<string, string> = { page: String(params.page ?? 1), per_page: String(params.per_page ?? 25) };
      if (params.search) query.search = params.search;
      if (params.journey_id != null) query.journey_id = String(params.journey_id);
      if (params.status) query.status = params.status;
      if (params.stalled) query.stalled = "true";
      const [json, status] = await makeRequest(undefined, query);
      return status >= 200 && status < 300 ? json : null;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [makeRequest]
  );
  return { loading, get, ready: Boolean(tenantId) };
}

export interface TimelineStep {
  id: string;
  label: string;
  type: string;
  state: "done" | "current" | "pending" | "skipped";
  depth: number;
  branch: string | null;
  completed_at: string | null;
  since_previous_seconds: number | null;
}

export interface ExecutionDetail extends ExecutionItem {
  journey_name: string;
  submitted_data: Record<string, any>;
  steps_completed: string[];
  variables: Record<string, any>;
  timeline: {
    total_steps: number;
    completed: number;
    remaining: number;
    skipped: number;
    percent_complete: number;
    steps: TimelineStep[];
  };
}

// GET /v1/executions/{session_id} — everything from the list row, plus the
// journey name, what was submitted, which steps ran, resolved variables and
// the full step timeline (including steps nested inside a condition's
// branches — `total_steps` counts those too, so a flat top-level count would
// under-report against what the canvas actually drew).
export function useExecutionDetail() {
  const tenantId = useTenantId();
  const { loading, makeRequest } = useJouneyReq("/v1/executions", "GET", authHeaders(tenantId));
  const get = useCallback(
    async (sessionId: string): Promise<ExecutionDetail | null> => {
      const [json, status] = await makeRequest(undefined, undefined, sessionId);
      return status >= 200 && status < 300 ? json : null;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [makeRequest]
  );
  return { loading, get, ready: Boolean(tenantId) };
}
